import moment from 'moment'
import i18next from 'i18next'
import {
  AddTradeRequest,
  NewPosicionPrecio,
  NewTramo,
  PosicionPrecio,
  PosicionPrecioDiff,
  TradeDetailsRequest,
  TradeDetailsResponse,
  TradeMainDetails,
  Tramo
} from '../../../api'
import { MeasureUnit } from '../../../api/Pioneer/model/MeasureUnit'
import { UnitPrice } from '../../../api/Pioneer/model/UnitPrice'
import { DatesInterval } from '../components/positionForm/positionForm'
import { checkCreatePastTradeRulebook, checkDateTimeLessThanRulebook } from '../trades.service'
import { diffPositionMapper } from '../trades.mapper'
import { getActiveTrade } from '../../../redux'

const defaultDateFormat = 'DD/MM/YYYY'

export interface tradesDates {
  startDate?: Date
  endDate?: Date
}

export const filterMeasureUnits = (
  currencyId: number,
  measureUnits: MeasureUnit[],
  unitPrices: UnitPrice[]
): MeasureUnit[] => {
  const unitPricesByCurrency = unitPrices.filter(x => x.currency === currencyId)
  const unitsFiltered = unitPricesByCurrency.flatMap(x => x.measureUnit)
  let measureUnitsFiltered = measureUnits.filter(x => unitsFiltered.includes(x.id))
  if (currencyId === null) {
    measureUnitsFiltered = measureUnits
  }
  return measureUnitsFiltered
}

export const getTotalQuantity = (formSt: Partial<AddTradeRequest | TradeMainDetails>) => {
  let total: number
  if (formSt.dailyQuantity && formSt.initDate && formSt.endDate) {
    let periodDays = moment(
      moment(formSt.endDate).format(defaultDateFormat),
      defaultDateFormat
    ).diff(moment(moment(formSt.initDate).format(defaultDateFormat), defaultDateFormat), 'days')
    periodDays = periodDays + 1
    total = periodDays * formSt.dailyQuantity > 0 ? periodDays * formSt.dailyQuantity : 0
  } else {
    total = formSt.dailyQuantity ?? 0
  }
  return total
}

export const isSimple = (trade: Partial<TradeDetailsResponse> | undefined): boolean => {
  return trade?.positions
    ? trade.positions.length === 1 &&
        trade.positions[0]?.tramos &&
        trade.positions[0].tramos.length === 1
    : true
}

export const tradeSimpleSameDates = (trade: Partial<TradeDetailsResponse> | undefined): boolean => {
  // Will return TRUE only if trade is simple AND if position dates and tract dates are the same.
  const tradeIsSimple = isSimple(trade)

  if (tradeIsSimple && trade?.positions) {
    if (
      trade?.initDate &&
      trade?.endDate &&
      (!moment(trade.initDate).isSame(moment(trade!.positions![0].fechaInicio), 'days') ||
        !moment(trade.endDate).isSame(moment(trade!.positions![0].fechaFin), 'days'))
    ) {
      return false
    }
    return (
      moment(trade!.positions![0].fechaInicio).isSame(
        moment(trade!.positions![0].tramos[0].fechaInicio),
        'days'
      ) &&
      moment(trade!.positions![0].fechaFin).isSame(
        moment(trade!.positions![0].tramos[0].fechaFin),
        'days'
      )
    )
  }
  return false
}

export const positionSimpleSameDates = (
  position: NewPosicionPrecio,
  tracts: (Tramo | NewTramo)[]
): boolean => {
  // Will return TRUE only if position is simple AND if position dates and tract dates are the same.
  const positionIsSimple = tracts.length === 1

  if (positionIsSimple) {
    if (
      moment(position?.fechaInicio).isSame(moment(tracts[0].fechaInicio), 'days') &&
      moment(position?.fechaFin).isSame(moment(tracts[0].fechaFin), 'days')
    ) {
      return true
    }
  }
  return false
}

export const getViewButtonDisabledState = (
  complexView: boolean,
  positions: NewPosicionPrecio[] | PosicionPrecio[] | null,
  tradeDates: tradesDates
): boolean => {
  let result = false
  if (positions) {
    result =
      complexView &&
      !tradeSimpleSameDates({
        initDate: tradeDates.startDate,
        endDate: tradeDates.endDate,
        positions: positions as PosicionPrecio[]
      })
  }
  return result
}

export const validatePositionDatesForTrade = (
  position: PosicionPrecio,
  tradeDates: DatesInterval
): boolean => {
  return (
    moment(position.fechaInicio).isSameOrAfter(moment(tradeDates.startDate), 'days') &&
    moment(position.fechaFin).isSameOrBefore(moment(tradeDates.endDate), 'days')
  )
}

export const getErrorText = (errorCodes: string): string => {
  const errors = errorCodes.split(',')
  let text = ''
  errors.forEach((error, idx) => {
    if (idx > 0) {
      text = text + ' '
    }
    text = text + i18next.t(`rulebook:${error}`)
  })
  return text
}

export interface checkDatesResponse {
  propsNeedToBeDisabled: boolean
  startDateIsPast: boolean
  endDateIsPast: boolean
}

export const checkDates = async (
  startDate: Date,
  endDate: Date,
  counterpart: number
): Promise<checkDatesResponse> => {
  let propsNeedToBeDisabled: boolean = false
  let isFutureInitDate: boolean | undefined = undefined
  let isFutureEndDate: boolean | undefined = undefined

  isFutureInitDate = startDate
    ? await checkDateTimeLessThanRulebook(startDate).then(response => response.success)
    : undefined
  isFutureEndDate = endDate
    ? await checkDateTimeLessThanRulebook(endDate).then(response => response.success)
    : undefined
  if (!isFutureInitDate || !isFutureEndDate) {
    const isMarketOrWholeseller = await checkCreatePastTradeRulebook(counterpart).then(res => {
      if (!res.success) {
        // Counterpart is "Market/Mercado" or "Wholeseller/Mayorista"
        return true
      } else return false
    })
    if (isMarketOrWholeseller) {
      propsNeedToBeDisabled = true
    }
  }

  return {
    startDateIsPast: !isFutureInitDate,
    endDateIsPast: !isFutureEndDate,
    propsNeedToBeDisabled: propsNeedToBeDisabled
  }
}

export const getPropsToUpdate = (data: PosicionPrecio): Partial<PosicionPrecioDiff> => {
  let diffPosition = diffPositionMapper(data)
  const trade = getActiveTrade()
  const position =
    trade && trade.positions && trade.positions.find(p => p.idPosicion === data.idPosicion)

  // Si la posición tiene precio, se comprueba si hubo cambio de período y se actualizan las fechas del precio.
  if (position?.price) {
    // Comparo con las fechas de la posición para determinar si hay una extensión del período de vigencia de la posición
    const { priceStartDate, priceEndDate } = getNewPriceDates(
      {
        newPositionStartDate: diffPosition?.fechaInicio,
        newPositionEndDate: diffPosition?.fechaFin
      },
      data.tramos
    )

    if (
      position?.fechaInicio &&
      priceStartDate &&
      !moment(position.fechaInicio).isSame(priceStartDate, 'day')
    ) {
      diffPosition = {
        ...diffPosition,
        price: { ...diffPosition.price, startDate: priceStartDate }
      }
    }

    if (
      position?.fechaFin &&
      priceEndDate &&
      !moment(position.fechaFin).isSame(priceEndDate, 'day')
    ) {
      diffPosition = { ...diffPosition, price: { ...diffPosition.price, endDate: priceEndDate } }
    }
  }

  return diffPosition
}

interface NewPositionDates {
  newPositionStartDate?: Date
  newPositionEndDate?: Date
}

export const getNewPriceDates = (
  newPositionDates: NewPositionDates,
  tracts?: (NewTramo | Tramo)[]
): { priceStartDate?: Date; priceEndDate?: Date } => {
  let startDate = newPositionDates.newPositionStartDate
  let endDate = newPositionDates.newPositionEndDate

  if (tracts) {
    // Busco mínima fecha inicio y máxima fecha fin de la lista de tramos
    const minInitDate = tracts.reduce((prev, current) =>
      moment(prev.fechaInicio).isSameOrBefore(current.fechaInicio, 'day') ? prev : current
    ).fechaInicio

    const maxEndDate = tracts.reduce((prev, current) =>
      moment(prev.fechaFin).isSameOrAfter(current.fechaFin, 'day') ? prev : current
    ).fechaFin

    startDate = startDate
      ? moment(minInitDate).isBefore(startDate, 'day')
        ? minInitDate
        : startDate
      : minInitDate

    endDate = endDate
      ? moment(maxEndDate).isAfter(endDate, 'day')
        ? maxEndDate
        : endDate
      : maxEndDate
  }

  return { priceStartDate: startDate, priceEndDate: endDate }
}

export const getUpdatePriceDates = (
  patchObject: Partial<TradeDetailsRequest> | undefined
): Partial<TradeDetailsRequest> | undefined => {
  if (patchObject?.fechaInicio || patchObject?.fechaFin) {
    const dates = {
      startDate: patchObject?.fechaInicio
        ? moment(patchObject?.fechaInicio).format('YYYY-MM-DD')
        : undefined,
      endDate: patchObject?.fechaFin
        ? moment(patchObject?.fechaFin).format('YYYY-MM-DD')
        : undefined
    }
    if (!patchObject.posicionesDelta || patchObject.posicionesDelta.length === 0) {
      patchObject = {
        ...patchObject,
        posicionesDelta: [
          {
            price: { ...dates }
          }
        ]
      }

      return patchObject
    }

    if (!patchObject.posicionesDelta[0].price) {
      patchObject = {
        ...patchObject,
        posicionesDelta: [
          {
            ...patchObject.posicionesDelta[0],
            price: { ...dates }
          }
        ]
      }

      return patchObject
    }

    return {
      ...patchObject,
      posicionesDelta: [
        {
          ...patchObject.posicionesDelta[0],
          price: {
            ...patchObject.posicionesDelta[0].price,
            ...dates
          }
        }
      ]
    }
  }

  return patchObject
}
